import { convertMeasure, isInteger, isMeasureWithValue, parseNumber, Validation } from '@e2m-customer-portal/shared';
import clsx from 'clsx';
import { UseInputFieldRules } from 'hooks/use-input-field-rules/use-input-field-rules';
import React, {
  ChangeEvent,
  memo,
  ReactElement,
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ForecastSlice } from '../../../../../../generated/graphql';
import { Direction } from '../../../../utils';
import { SelectedCell } from '../../ForecastList';
import { isKeyCombo } from '../../utils';
import { SelectHandle } from '../DeviceTable';
import { dispatchNativeInputEvent } from '../utils';

interface CellInputProps {
  deviceId: string;
  slice: ForecastSlice;
  powerLevelRules: Validation<string | number> | undefined;
  rules: UseInputFieldRules | undefined;
  disabled?: boolean;
  readOnly?: boolean;
  cellIndex: {
    row: number;
    column: number;
  };
  value?: string;
  preview?: string;
  deviceMap: (HTMLInputElement | null)[][];
  selectedCells?: SelectedCell[];
  shouldSyncIntradaySisterCells?: boolean;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  setError: React.Dispatch<React.SetStateAction<string | undefined>>;
  setOverviewMap: React.Dispatch<React.SetStateAction<Map<string, Array<Array<HTMLInputElement | null>>>>>;
  switchFocus: (sourceRow: number, sourceColumn: number, direction?: Direction) => boolean;
  setSelectHandle?: React.Dispatch<React.SetStateAction<SelectHandle>>;
  setSelectedCells?: React.Dispatch<React.SetStateAction<SelectedCell[]>>;
  handleSelectAll?: () => void;
  pasteCopiedCells?: (event: React.KeyboardEvent<HTMLInputElement>, startRow: number, startColumn: number) => void;
  saveChanges: () => void;
}

function CellInputMemo({
  deviceId,
  slice,
  powerLevelRules,
  rules,
  disabled,
  readOnly,
  cellIndex,
  value,
  preview,
  deviceMap,
  selectedCells,
  shouldSyncIntradaySisterCells,
  setError,
  setValue,
  setOverviewMap,
  switchFocus,
  setSelectHandle,
  setSelectedCells,
  handleSelectAll,
  pasteCopiedCells,
  saveChanges,
}: Readonly<CellInputProps>): ReactElement<CellInputProps> {
  const [t] = useTranslation('validation');
  const [inputValue, setInputValue] = useState(value);
  const valueDebounce = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    if (!readOnly && !disabled) event.currentTarget.select();
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const resetSelectHandle = useCallback(() => {
    if (!selectedCells?.length || !setSelectHandle || !setSelectedCells) return;
    startTransition(() => {
      setSelectHandle((selectHandle) => ({ ...selectHandle, active: false }));
      setSelectedCells([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCells?.length]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;
      setInputValue(rawValue);

      if (shouldSyncIntradaySisterCells) {
        for (let i = 0; i < 4; i++) {
          const row = cellIndex.row - (cellIndex.row % 4) + i;
          if (cellIndex.row !== row) dispatchNativeInputEvent(deviceMap[row][cellIndex.column], rawValue);
        }
      }

      if (rawValue === '') {
        setError(t('COMMON_REQUIRED'));
      } else if (!isInteger(rawValue) || 0 > parseNumber(rawValue)) {
        setError(t('COMMON_MUST_CONTAIN_POSITIVE_INTEGER'));
        //validation rules: PROD_15 - PROD_16
      } else if (powerLevelRules && !powerLevelRules.validate(parseNumber(rawValue))) {
        setError(powerLevelRules.message);
      } else if (
        rules &&
        isMeasureWithValue(rules.maxValue) &&
        convertMeasure(rules.maxValue, 'kW').value < parseNumber(rawValue)
      ) {
        setError(rules.rules[0].message);
      } else if (
        rules &&
        isMeasureWithValue(rules.minValue) &&
        convertMeasure(rules.minValue, 'kW').value > parseNumber(rawValue) &&
        parseNumber(rawValue) !== 0
      ) {
        setError(rules.rules[1].message);
      } else {
        setError(undefined);
      }
      clearTimeout(valueDebounce.current);
      valueDebounce.current = setTimeout(() => setValue(event.target.value), 250);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rules, slice],
  );

  const handleEscape = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      startTransition(() => {
        if (event.currentTarget.selectionStart !== event.currentTarget.selectionEnd) {
          event.currentTarget.setSelectionRange(event.currentTarget.selectionEnd, event.currentTarget.selectionEnd);
        } else if (selectedCells?.length) {
          resetSelectHandle();
        } else {
          event.currentTarget.blur();
        }
        event.preventDefault();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCells?.length],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      startTransition(() => {
        if (event.key === 'Escape') {
          handleEscape(event);
        } else if ((Object.values(Direction) as string[]).includes(event.key)) {
          resetSelectHandle();
          if (switchFocus(cellIndex.row, cellIndex.column, event.key as Direction)) {
            event.preventDefault();
          }
        } else if (event.key === 'Enter') {
          resetSelectHandle();
          event.currentTarget.select();
          switchFocus(cellIndex.row, cellIndex.column, event.shiftKey ? Direction.Up : Direction.Down);
          event.preventDefault();
        } else if (event.key === 'Tab') {
          resetSelectHandle();
          event.currentTarget.select();
          switchFocus(cellIndex.row, cellIndex.column, event.shiftKey ? Direction.Left : Direction.Right);
          event.preventDefault();
        } else if (isKeyCombo(event)) {
          if (isKeyCombo(event, 'a')) {
            if (handleSelectAll) handleSelectAll();
            event.preventDefault();
          } else if (isKeyCombo(event, 'c')) {
            const sortedCells = selectedCells?.length
              ? [...selectedCells].sort((a, b) => a.column - b.column).sort((a, b) => a.row - b.row)
              : [{ column: cellIndex.column, row: cellIndex.row, value }];
            window.localStorage.setItem('COPIED_PRODUCTION', JSON.stringify(sortedCells));
            if (setSelectHandle)
              setSelectHandle((selectHandle) => ({
                ...selectHandle,
                selectionType: 'copy',
              }));
            event.preventDefault();
          } else if (isKeyCombo(event, 'v')) {
            if (pasteCopiedCells) pasteCopiedCells(event, cellIndex.row, cellIndex.column);
          } else if (isKeyCombo(event, 's')) {
            saveChanges();
            event.preventDefault();
          }
        }
      });
    },
    [
      handleEscape,
      resetSelectHandle,
      switchFocus,
      cellIndex.row,
      cellIndex.column,
      handleSelectAll,
      selectedCells,
      value,
      setSelectHandle,
      pasteCopiedCells,
      saveChanges,
    ],
  );

  const collectRef = useCallback(
    (ref: HTMLInputElement) =>
      startTransition(() => {
        setOverviewMap((overviewMap) => {
          const deviceMap = overviewMap.get(deviceId)!;
          deviceMap[cellIndex.row][cellIndex.column] = ref;
          return overviewMap;
        });
      }),
    [cellIndex.column, cellIndex.row, deviceId, setOverviewMap],
  );

  const inputClasses =
    'w-[calc(100%-4rem)] leading-6 px-9 py-0 border-none text-center text-black bg-transparent focus:outline-none focus:cursor-text';

  return (
    <>
      <input
        ref={collectRef}
        className={clsx(inputClasses, {
          invisible: preview !== undefined,
          'text-opacity-60': disabled,
          'cursor-cell': !readOnly && !disabled,
        })}
        value={inputValue}
        readOnly={readOnly || disabled}
        disabled={disabled}
        onMouseDown={handleFocus}
        onFocus={handleFocus}
        onBlur={resetSelectHandle}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {preview !== undefined && (
        <input
          className={clsx('absolute', inputClasses, { 'text-opacity-60': disabled })}
          defaultValue={readOnly || disabled ? value : preview}
          readOnly={readOnly || disabled}
        />
      )}
    </>
  );
}

export const CellInput = memo(CellInputMemo);
